import { faArrowTrendDown } from '@fortawesome/pro-solid-svg-icons/faArrowTrendDown';
import { faArrowTrendUp } from '@fortawesome/pro-solid-svg-icons/faArrowTrendUp';
import { faClockRotateLeft } from '@fortawesome/pro-solid-svg-icons/faClockRotateLeft';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GetItemCounts, TransactionEntryType, useInfiniteClientTransactions } from '@ukpostbox/client-data';
import {
	Card,
	CardLayoutType,
	ContentBlock,
	ContentLoader,
	FlexAlign,
	FlexJustify,
	InfiniteLoadTrigger,
	ItemRow,
	StackHorizontal,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { FormatCurrency, FormatLocaleDate } from '@ukpostbox/helpers';
import React, { useEffect, useRef, useState } from 'react';
import { useSite } from '../../../_Global/Providers/SiteInterfaceProvider';

type TransactionGroup = {
	balance: number;
	date: Date;
	data: Array<TransactionEntryType>;
};

function Transactions() {
	const { t } = useTranslation();
	const { currentLocale, updatePageMeta } = useSite();

	const { data, status, error, isFetchingNextPage, fetchNextPage, hasNextPage, isStale, isRefetching } =
		useInfiniteClientTransactions();

	const [itemCounts, setItemCounts] = useState<any>({ loaded: 0, total: 0 });
	const [groupedData, setGroupedData] = useState<any>(null);

	const wrapperRef = useRef<any>(null);

	const FormatTitle = (description: string) => {
		let newTitle = description;
		const formatDesc = newTitle.toLowerCase();

		if (formatDesc !== 'top-up') {
			if (formatDesc.indexOf('parcel received') > -1) {
				newTitle = 'Parcel Received';
			} else if (formatDesc.indexOf('contents scan') > -1) {
				newTitle = 'Contents Scan';
			} else if (formatDesc.indexOf('automated charge') > -1) {
				newTitle = 'Automatic Charge';
			} else if (formatDesc.indexOf('letter received') > -1) {
				newTitle = 'Letter Received';
			}
		}

		return newTitle;
	};

	const FormatDescription = (description: string) => {
		let newDescription = description;
		const formatDesc = newDescription.toLowerCase();

		if (formatDesc !== 'top-up') {
			if (formatDesc.indexOf('parcel received') > -1) {
				newDescription = newDescription.replace(
					newDescription.substring(
						formatDesc.indexOf('parcel received'),
						formatDesc.indexOf('parcel received') + 'parcel received'.length,
					),
					'',
				);
			} else if (formatDesc.indexOf('contents scan') > -1) {
				newDescription = newDescription.replace(
					newDescription.substring(
						formatDesc.indexOf('contents scan'),
						formatDesc.indexOf('contents scan') + 'contents scan'.length,
					),
					'',
				);
			} else if (formatDesc.indexOf('automated charge') > -1) {
				newDescription = newDescription.replace(
					newDescription.substring(
						formatDesc.indexOf('automated charge'),
						formatDesc.indexOf('automated charge') + 'automated charge'.length,
					),
					'',
				);
			} else if (formatDesc.indexOf('letter received') > -1) {
				newDescription = newDescription.replace(
					newDescription.substring(
						formatDesc.indexOf('letter received'),
						formatDesc.indexOf('letter received') + 'letter received'.length,
					),
					'',
				);
			}
		} else {
			newDescription = '';
		}

		return newDescription;
	};

	const FormatData = () => {
		if (!data) return;

		const flatData = data?.pages.map((page) => page.data.filter((item) => item)).flat();

		const groups = flatData?.reduce((group: any, item) => {
			const newGroup = window.structuredClone(group);
			const date = item.transactionDate.toString().split('T')[0];

			if (!newGroup[date]) {
				newGroup[date] = [];
			}

			newGroup[date].push(item);

			return newGroup;
		}, {});

		const groupArrays = Object.keys(groups).map((date) => ({
			date,
			balance: groups[date][0].balance,
			data: groups[date],
		}));

		setGroupedData(groupArrays);
		setItemCounts(GetItemCounts(data));
	};

	useEffect(() => {
		if (data) {
			FormatData();
		}
	}, [data]);

	useEffect(() => {
		updatePageMeta({
			title: t('settings.transactions.title'),
		});
	}, [currentLocale]);

	return (
		<>
			<StackHorizontal align={FlexAlign.Center} justify={FlexJustify.Between} extraClass='mb-md'>
				<h2 className='mb-0'>{t('settings.transactions.title')}</h2>
			</StackHorizontal>

			<div ref={wrapperRef} className='flex-column flex-gap-lg'>
				{status === 'pending' ? (
					<ContentLoader childHeight='6.7em' round />
				) : status === 'error' ? (
					<ContentBlock error title={error.name} content={error.message} />
				) : groupedData ? (
					<>
						{groupedData.map((group: TransactionGroup) => (
							<div>
								<Card
									header={{
										sticky: true,
										title: FormatLocaleDate(group.date, currentLocale),
										addonEnd: <h5 className='m-0'>{FormatCurrency(group.balance)}</h5>,
									}}
									type={CardLayoutType.SlimHeader}
									emptyBody
								>
									{group.data.map((item) => (
										<ItemRow
											title={`Transactions Entry ${item.transactionId}`}
											key={`transactionItem${item.transactionId}`}
											static
										>
											<div className='item-row-media'>
												<FontAwesomeIcon
													icon={
														item.reversed
															? faClockRotateLeft
															: item.credit
																? faArrowTrendUp
																: faArrowTrendDown
													}
												/>
											</div>
											<div>{FormatTitle(item.description)}</div>
											<div className='text-right'>
												<h6 className='m-0'>
													{item.credit ? '+' : '-'}
													{item.credit
														? FormatCurrency(item.credit)
														: FormatCurrency(item.debit)}
												</h6>
												{!item.credit && item.tax && (
													<div>
														{FormatCurrency(item.tax)} {t('labels.tax')}
													</div>
												)}
											</div>
										</ItemRow>
									))}
								</Card>
							</div>
						))}

						<InfiniteLoadTrigger
							loaded={itemCounts.loaded}
							totalCount={itemCounts.total}
							hasNextPage={hasNextPage}
							fetchNextPage={fetchNextPage}
							isStale={isStale}
							isRefetching={isRefetching}
							isFetchingNextPage={isFetchingNextPage}
						/>
					</>
				) : (
					<div />
				)}
			</div>
		</>
	);
}

export default Transactions;
